import React, { useEffect, useMemo, useState } from 'react';
import { millProductionAPI } from '../../services/millAPI';
import { batchShipsInRange, computeReorder } from './batchPlanLogic';
import { agingColor } from './bankAging';

// ── Types (mirror ProductionBatchViewSet.list rows) ──────────────────────────
interface PlanOrder {
  order_number: string;
  customer_name: string;
  delivery_date: string | null;
  allocated_weight: number;
}
export interface PlanBatch {
  id: number;
  batch_number: string;
  formula_name: string;
  feed_form: string;
  feed_form_display: string;
  planned_weight: number;
  planned_date: string | null;
  priority: number;
  orders_count: number;
  ship_dates: string[];   // ISO YYYY-MM-DD, sorted
  orders: PlanOrder[];
  bank_available_lbs: number;   // FIFO bank available (formula+form), capped at size
  bank_oldest_age_days: number | null;   // age of the oldest matching lot (FIFO next)
}

// ── Date helpers (local, no TZ shift) ────────────────────────────────────────
const toISO = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDays = (d: Date, n: number): Date => {
  const r = new Date(d);
  r.setDate(d.getDate() + n);
  return r;
};
// Default window = the current SHIPPING WEEK: Tuesday–Friday of the week
// containing today. Orders ship within operating days, so Tue–Fri catches all
// in-week batches. Adjustable via the presets / pickers.
const shippingWeek = (): { from: string; to: string } => {
  const today = new Date();
  const dow = today.getDay();                       // 0=Sun … 6=Sat
  const monday = addDays(today, -((dow + 6) % 7));   // back up to Monday
  return { from: toISO(addDays(monday, 1)), to: toISO(addDays(monday, 4)) };  // Tue..Fri
};

interface Props {
  onClose: () => void;
  onStartMixing: (batchId: number) => void;
  onSaved?: () => void;   // refresh the parent (Dashboard) after a priority Save
}

const BatchPlanModal: React.FC<Props> = ({ onClose, onStartMixing, onSaved }) => {
  const [batches, setBatches] = useState<PlanBatch[]>([]);
  // The last server-persisted id sequence — baseline for unsaved-changes / discard.
  const [savedOrder, setSavedOrder] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const initial = shippingWeek();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [pullingId, setPullingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [sizeEdits, setSizeEdits] = useState<Record<number, string>>({});
  const [savingSizeId, setSavingSizeId] = useState<number | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await millProductionAPI.getBatches({ status: 'PLANNED' });
      // Server already sorts priority → planned_date → formula → created_at.
      const rows: PlanBatch[] = res.data.results || [];
      setBatches(rows);
      setSavedOrder(rows.map(b => b.id));   // baseline = freshly-loaded (= persisted) order
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const confirmBankPull = async (batch: PlanBatch) => {
    // A pull reloads the list, which would drop an unsaved drag. Warn first so
    // Zion doesn't lose the arrangement unknowingly (don't block — let him proceed).
    if (dirty && !window.confirm(
      'You have unsaved order changes that will be lost. Save your order first, or continue and discard them?'
    )) {
      return;
    }
    setPullingId(batch.id);
    setSaveMsg(null);
    try {
      const res = await millProductionAPI.confirmBankPull(batch.id);
      const pulled = res.data?.pulled_lbs || 0;
      await load();   // re-fetch: batch shrank, bank availability reduced
      setSaveMsg(pulled > 0
        ? { kind: 'success', text: `Pulled ${pulled.toLocaleString()} lbs from the bank — batch now mills only the top-up.` }
        : { kind: 'success', text: 'No banked feed pulled (already sourced or none available).' });
    } catch (e: any) {
      setSaveMsg({ kind: 'error', text: e?.response?.data?.error || 'Bank pull failed.' });
    } finally {
      setPullingId(null);
    }
  };

  const visible = useMemo(
    () => batches.filter(b => batchShipsInRange(b.ship_dates, from, to)),
    [batches, from, to]);

  // ── Bulk-select for archive (curate which batches stay in the planner) ──────
  const allVisibleSelected = visible.length > 0 && visible.every(b => selected.has(b.id));
  const toggleRow = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAllVisible = () => setSelected(prev => {
    const next = new Set(prev);
    if (allVisibleSelected) visible.forEach(b => next.delete(b.id));   // clear visible
    else visible.forEach(b => next.add(b.id));                          // select visible
    return next;
  });
  const selectedBatches = useMemo(
    () => batches.filter(b => selected.has(b.id)),
    [batches, selected]);

  const archiveSelected = async () => {
    const ids = Array.from(selected);
    setArchiving(true);
    setSaveMsg(null);
    try {
      const res = await millProductionAPI.archiveBatches(ids);
      const skipped = res.data?.skipped || [];
      setConfirmArchive(false);
      setSelected(new Set());
      await load();        // re-fetch: archived rows drop out (status no longer PLANNED)
      onSaved?.();         // refresh the Dashboard
      setSaveMsg(skipped.length > 0
        ? { kind: 'error', text: `Archived ${res.data.archived}. ${skipped.length} skipped (not in PLANNED state).` }
        : { kind: 'success', text: `Archived ${res.data.archived} batch${res.data.archived !== 1 ? 'es' : ''} — hidden from the planner (reversible).` });
    } catch (e: any) {
      setSaveMsg({ kind: 'error', text: e?.response?.data?.error || 'Could not archive the selected batches.' });
    } finally {
      setArchiving(false);
    }
  };

  // ── Drag-reorder is LOCAL until Save (Brad: arrange → review → commit) ──────
  // Unsaved iff the working id sequence differs from the persisted baseline.
  const dirty = useMemo(
    () => batches.map(b => b.id).join(',') !== savedOrder.join(','),
    [batches, savedOrder]);

  const handleDrop = (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggedId == null || draggedId === targetId) { setDraggedId(null); return; }
    // before/after the target by cursor position relative to the row midpoint.
    const rect = e.currentTarget.getBoundingClientRect();
    const placeAfter = e.clientY > rect.top + rect.height / 2;
    // Operate on the FULL id list so hidden/filtered batches keep their relative
    // order (global-order-safe, 3-3). Reorder LOCALLY only — no API call yet.
    const newFull = computeReorder(batches.map(b => b.id), draggedId, targetId, placeAfter);
    const byId = new Map(batches.map(b => [b.id, b]));
    setBatches(newFull.map(id => byId.get(id)).filter(Boolean) as PlanBatch[]);
    setDraggedId(null);
    setSaveMsg(null);
  };

  // SAVE commits the full working sequence (priority = index over the whole list).
  const saveOrder = async () => {
    const ids = batches.map(b => b.id);
    setSaving(true);
    setSaveMsg(null);
    try {
      await millProductionAPI.reorderBatches(ids);
      setSavedOrder(ids);   // new baseline → dirty clears
      setSaveMsg({ kind: 'success', text: 'Sequence saved — the Dashboard and Command plan now flow this order.' });
      onSaved?.();          // refresh the parent (Dashboard) list
    } catch (e: any) {
      setSaveMsg({ kind: 'error', text: e?.response?.data?.error || 'Could not save the new order.' });
    } finally {
      setSaving(false);
    }
  };

  // DISCARD drops unsaved drags by restoring the persisted baseline order.
  const discardOrder = () => {
    const byId = new Map(batches.map(b => [b.id, b]));
    setBatches(savedOrder.map(id => byId.get(id)).filter(Boolean) as PlanBatch[]);
    setSaveMsg(null);
  };

  // ── Per-row batch-size edit (persists planned_weight via adjust_batch_size) ──
  const sizeValue = (b: PlanBatch) => sizeEdits[b.id] ?? String(b.planned_weight);
  const sizeChanged = (b: PlanBatch) => {
    const v = Number(sizeValue(b));
    return Number.isFinite(v) && v > 0 && v !== b.planned_weight;
  };
  const saveSize = async (b: PlanBatch) => {
    const newWeight = Number(sizeValue(b));
    if (!Number.isFinite(newWeight) || newWeight <= 0) {
      setSaveMsg({ kind: 'error', text: 'Batch size must be a positive number.' });
      return;
    }
    setSavingSizeId(b.id);
    setSaveMsg(null);
    try {
      await millProductionAPI.adjustBatchSize(b.id, { new_weight: newWeight });
      // Patch locally so the row reflects the saved size without a full reload.
      setBatches(prev => prev.map(x => x.id === b.id ? { ...x, planned_weight: newWeight } : x));
      setSizeEdits(prev => { const n = { ...prev }; delete n[b.id]; return n; });
      setSaveMsg({ kind: 'success', text: `Batch ${b.batch_number} resized to ${newWeight.toLocaleString()} lbs.` });
    } catch (e: any) {
      setSaveMsg({ kind: 'error', text: e?.response?.data?.error || 'Could not resize the batch.' });
    } finally {
      setSavingSizeId(null);
    }
  };

  const setPreset = (preset: 'today' | 'week' | 'two' | 'three') => {
    const today = new Date();
    if (preset === 'today') { setFrom(toISO(today)); setTo(toISO(today)); }
    else if (preset === 'week') { const w = shippingWeek(); setFrom(w.from); setTo(w.to); }
    else if (preset === 'two') { setFrom(toISO(today)); setTo(toISO(addDays(today, 13))); }
    else if (preset === 'three') { setFrom(toISO(today)); setTo(toISO(addDays(today, 20))); }
  };

  const fmt = (iso: string | null) => iso
    ? new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '—';

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>Plan / Prioritize</h2>
          <button style={styles.close} onClick={onClose}>&times;</button>
        </div>

        {/* Date-range filter — a VIEW lens only; it does NOT change priority or
            stop the mill (the mill flows the global sequence past the range). */}
        <div style={styles.filterBar}>
          <div style={styles.presets}>
            <button className="mill-btn mill-btn-sm" onClick={() => setPreset('today')}>Today</button>
            <button className="mill-btn mill-btn-sm" onClick={() => setPreset('week')}>This week</button>
            <button className="mill-btn mill-btn-sm" onClick={() => setPreset('two')}>Next 2 weeks</button>
            <button className="mill-btn mill-btn-sm" onClick={() => setPreset('three')}>Next 3 weeks</button>
          </div>
          <div style={styles.range}>
            <label style={styles.lbl}>From <input type="date" value={from} max={to}
              onChange={e => setFrom(e.target.value)} style={styles.dateInput} /></label>
            <label style={styles.lbl}>To <input type="date" value={to} min={from}
              onChange={e => setTo(e.target.value)} style={styles.dateInput} /></label>
          </div>
        </div>
        <p style={styles.viewNote}>
          Showing planned batches shipping <strong>{fmt(from)}–{fmt(to)}</strong> · {visible.length} of {batches.length}.
          This is a view filter — the mill still flows the full priority sequence past this range.
        </p>

        <div style={styles.body}>
          {loading ? <div style={styles.msg}>Loading…</div>
            : error ? <div style={{ ...styles.msg, color: '#b91c1c' }}>{error}</div>
            : visible.length === 0 ? <div style={styles.msg}>No planned batches ship in this range.</div>
            : (
              <table className="mill-table">
                  <thead>
                    <tr>
                      <th style={{ ...styles.stickyTh, width: 30 }} title="Select all shown">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleAllVisible}
                          aria-label="Select all shown batches"
                        />
                      </th>
                      <th style={{ ...styles.stickyTh, width: 28 }} title="Drag a row to reprioritize">↕</th>
                      <th style={styles.stickyTh}>Batch #</th>
                      <th style={styles.stickyTh}>Formula</th>
                      <th style={styles.stickyTh}>Form</th>
                      <th style={styles.stickyTh}>Size (editable)</th>
                      <th style={styles.stickyTh}>Order(s) / Farm</th>
                      <th style={styles.stickyTh}>Ship date(s)</th>
                      <th style={styles.stickyTh}>Planned</th>
                      <th style={styles.stickyTh}>Bank (FIFO)</th>
                      <th style={styles.stickyTh}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(b => (
                      <tr
                        key={b.id}
                        // Drop target: dragover/dragenter MUST preventDefault or the
                        // browser rejects the drop (no-drop cursor, onDrop never fires).
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                        onDragEnter={(e) => e.preventDefault()}
                        onDrop={(e) => handleDrop(e, b.id)}
                        style={{ opacity: draggedId === b.id ? 0.4 : 1, background: selected.has(b.id) ? '#eff6ff' : undefined }}
                      >
                        {/* Select-for-archive checkbox — NOT draggable, keeps the row's
                            interactive cells clickable; drag stays isolated to the grip. */}
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selected.has(b.id)}
                            onChange={() => toggleRow(b.id)}
                            aria-label={`Select batch ${b.batch_number}`}
                          />
                        </td>
                        {/* Drag handle — ONLY the grip is draggable, so size/inputs in the
                            row stay clickable. setData() is required for Firefox/Safari to
                            emit drop events. */}
                        <td
                          draggable={!saving}
                          onDragStart={(e) => {
                            setDraggedId(b.id);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(b.id));
                          }}
                          onDragEnd={() => setDraggedId(null)}
                          style={{ color: '#64748b', cursor: 'grab', textAlign: 'center', userSelect: 'none' }}
                          title="Drag to reprioritize (sets the mill's run order)"
                        >↕</td>
                        <td><strong>{b.batch_number}</strong></td>
                        <td className="formula-cell"><strong>{b.formula_name}</strong></td>
                        <td>
                          <span className={`feed-form-badge form-${(b.feed_form || '').toLowerCase()}`}>
                            {b.feed_form}
                          </span>
                        </td>
                        <td className="weight-cell">
                          <div style={styles.sizeCell}>
                            <input
                              type="number"
                              min={1}
                              step={100}
                              value={sizeValue(b)}
                              onChange={(e) => setSizeEdits(prev => ({ ...prev, [b.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter' && sizeChanged(b)) saveSize(b); }}
                              style={styles.sizeInput}
                              title="Edit the batch size — saves planned_weight + recalculates ingredients"
                            />
                            <span style={styles.sizeUnit}>lbs</span>
                            {sizeChanged(b) && (
                              <button
                                className="mill-btn mill-btn-sm"
                                disabled={savingSizeId === b.id}
                                onClick={() => saveSize(b)}
                                title="Save the new batch size"
                              >
                                {savingSizeId === b.id ? 'Saving…' : 'Save'}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          {b.orders.map((o, i) => (
                            <div key={i} style={styles.orderLine}>
                              <span style={styles.ordNum}>{o.order_number}</span>
                              <span style={styles.farm}>{o.customer_name}</span>
                              <span style={styles.ordDate}>{fmt(o.delivery_date)}</span>
                              <span style={styles.ordLbs}>{o.allocated_weight.toLocaleString()} lbs</span>
                            </div>
                          ))}
                          {b.orders.length === 0 && <span style={{ color: '#94a3b8' }}>— stock —</span>}
                        </td>
                        <td>{b.ship_dates.map(fmt).join(', ') || '—'}</td>
                        <td>{fmt(b.planned_date)}</td>
                        <td>
                          {b.bank_available_lbs > 0 ? (
                            <div style={styles.bankCell}>
                              <span style={styles.bankLbs}>
                                {b.bank_available_lbs.toLocaleString()} lbs
                                {b.bank_oldest_age_days != null && (
                                  <span style={{ color: agingColor(b.bank_oldest_age_days), fontWeight: 700, marginLeft: 6 }}>
                                    · {b.bank_oldest_age_days}d
                                  </span>
                                )}
                              </span>
                              <button
                                className="mill-btn mill-btn-sm"
                                disabled={pullingId === b.id}
                                onClick={() => confirmBankPull(b)}
                                title="Pull this banked feed (FIFO) and mill only the top-up"
                              >
                                {pullingId === b.id ? 'Pulling…' : 'Confirm pull'}
                              </button>
                            </div>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td>
                          <button className="mill-btn mill-btn-sm mill-btn-success"
                                  onClick={() => onStartMixing(b.id)}>
                            Start Mixing
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            )}
        </div>

        <div style={styles.footer}>
          <span style={styles.soon}>
            {saving ? 'Saving order…'
              : dirty ? <span style={{ color: '#b45309', fontWeight: 600, fontStyle: 'normal' }}>● Unsaved order changes — click Save to commit the sequence.</span>
              : saveMsg ? <span style={{ color: saveMsg.kind === 'success' ? '#15803d' : '#b91c1c' }}>{saveMsg.text}</span>
              : 'Drag the ↕ grip to set the mill run order · edit Size to resize a batch · Confirm pull ships banked feed and mills only the top-up.'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="mill-btn mill-btn-secondary"
              onClick={() => setConfirmArchive(true)}
              disabled={saving || archiving || selected.size === 0}
              title="Archive the selected batches (hidden from the planner, reversible)"
            >
              Archive selected ({selected.size})
            </button>
            {dirty && (
              <button className="mill-btn mill-btn-secondary" onClick={discardOrder} disabled={saving}>
                Discard changes
              </button>
            )}
            <button className="mill-btn mill-btn-success" onClick={saveOrder} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save order'}
            </button>
            <button className="mill-btn mill-btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>

        {/* Archive confirmation — lists the selected batch numbers */}
        {confirmArchive && (
          <div style={styles.confirmOverlay} onClick={() => !archiving && setConfirmArchive(false)}>
            <div style={styles.confirmBox} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 8px' }}>
                Archive {selectedBatches.length} batch{selectedBatches.length !== 1 ? 'es' : ''}?
              </h3>
              <p style={{ margin: '0 0 10px', color: '#475569', fontSize: 13.5 }}>
                They'll be hidden from the planner (reversible via the
                <code> archive_stale_batches --unarchive</code> command). Only PLANNED
                batches are archived; anything already in production is skipped.
              </p>
              <div style={styles.confirmList}>
                {selectedBatches.map(b => (
                  <div key={b.id} style={{ fontSize: 13 }}>
                    <strong>{b.batch_number}</strong> · {b.formula_name} · {b.planned_weight.toLocaleString()} lbs
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button className="mill-btn mill-btn-secondary" onClick={() => setConfirmArchive(false)} disabled={archiving}>
                  Cancel
                </button>
                <button className="mill-btn mill-btn-success" onClick={archiveSelected} disabled={archiving}>
                  {archiving ? 'Archiving…' : `Archive ${selectedBatches.length}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 16, overflowY: 'auto' },
  modal: { background: '#fff', borderRadius: 8, width: '95vw', maxWidth: 1500, marginTop: 24,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)' },
  confirmOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 },
  confirmBox: { background: '#fff', borderRadius: 8, padding: 18, width: '100%', maxWidth: 460,
    boxShadow: '0 10px 40px rgba(0,0,0,0.35)' },
  confirmList: { maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: 8, display: 'flex', flexDirection: 'column', gap: 3, background: '#f8fafc' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px', borderBottom: '1px solid #e5e7eb' },
  close: { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: '#6b7280' },
  filterBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: 10, padding: '12px 18px 0' },
  presets: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  range: { display: 'flex', gap: 12 },
  lbl: { fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 },
  dateInput: { padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
  viewNote: { padding: '6px 18px 0', fontSize: 12.5, color: '#6b7280' },
  body: { padding: '10px 18px', maxHeight: '60vh', overflow: 'auto' },
  msg: { padding: 24, textAlign: 'center', color: '#6b7280' },
  // Sticky column headers — stay visible as the batch list scrolls vertically.
  stickyTh: { position: 'sticky', top: 0, zIndex: 2, background: '#f8fafc',
    boxShadow: 'inset 0 -1px 0 #e5e7eb' },
  sizeCell: { display: 'flex', alignItems: 'center', gap: 6 },
  sizeInput: { width: 78, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4,
    fontSize: 13, fontWeight: 700, textAlign: 'right' },
  sizeUnit: { fontSize: 12, color: '#64748b' },
  orderLine: { display: 'flex', gap: 8, fontSize: 12.5, whiteSpace: 'nowrap' },
  ordNum: { fontWeight: 600 },
  farm: { color: '#475569' },
  ordDate: { color: '#94a3b8' },
  ordLbs: { color: '#0f766e', fontWeight: 600, marginLeft: 'auto', paddingLeft: 10 },
  bankCell: { display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' },
  bankLbs: { fontSize: 12.5, fontWeight: 600, color: '#0f766e' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 18px', borderTop: '1px solid #e5e7eb' },
  soon: { fontSize: 12, color: '#94a3b8', fontStyle: 'italic' },
};

export default BatchPlanModal;
